import {createContext, useContext} from 'react'
import {LngLatLike, Map} from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import {Object3D} from 'three'
import {ObjectOfMapInterface} from '@/types/types'
import {OrbObject3D} from './ThreeJs/OrbLoader'
import {DroneConfig, DroneType} from './ThreeJs/DroneLoader'

export type {DroneType, DroneConfig}

export interface AddedOrbData {
  id: number
  img: string
  refId?: number
  kind?: 'orb' | 'miner'
  data: ObjectOfMapInterface
}

export interface PlacingOrbCallbacks {
  onPlace: (
    coordinates: [number, number],
    screenPoint: {x: number; y: number}
  ) => void
  onCancel: () => void
}

export interface CollectOrbParams {
  orbId: number
  hexId: string
  coordinates: [number, number]
  bounds: [number, number][]
  orbData: ObjectOfMapInterface
  refId?: number
}

export interface CollectOrbResult {
  success: boolean
  orb?: OrbObject3D | null
  error?: string
  localCode?: number
}

export interface MapContextType {
  mapInstance: Map | null
  isMapReady: boolean
  initializeMap: (
    containerId: string,
    dataOfMap: {lat: number; lng: number; zoom: number}
  ) => Map | null
  destroyMap: () => void
  flyTo: (coordinates: LngLatLike, zoom?: number) => void
  getMap: () => Map | null
  updatePitch: () => void
  addObject: (coords: [number, number][]) => Object3D | null
  clearObjects: () => void
  hoveredHexId: string | null
  setHoveredHexId: (id: string | null) => void
  show3DObjects: () => void
  hide3DObjects: () => void
  addOrb: (
    coords: [number, number][],
    orbData: ObjectOfMapInterface,
    hexId: string
  ) => OrbObject3D | null
  removeOrb: (hexId: string) => void
  clearOrbs: () => void
  getOrbByHexId: (hexId: string) => OrbObject3D | null
  hoveredOrbHexId: string | null
  setHoveredOrbHexId: (hexId: string | null) => void
  hoveredOrbData: ObjectOfMapInterface | null
  startPlacingOrb: (
    orbData: AddedOrbData,
    callbacks: PlacingOrbCallbacks
  ) => void
  stopPlacingOrb: () => void
  isPlacingOrb: boolean
  placingOrbData: AddedOrbData | null
  collectOrb: (params: CollectOrbParams) => Promise<CollectOrbResult>
  addRedSphere: (coords: [number, number][]) => Object3D | null
  clearRedSpheres: () => void
  addOnSaleObject: (coords: [number, number][]) => Object3D | null
  clearOnSaleObjects: () => void
  removeOnSaleObject: (object: Object3D) => void
  addOnRentedObject: (coords: [number, number][]) => Object3D | null
  clearOnRentedObjects: () => void
  removeOnRentedObject: (object: Object3D) => void
  addHalfSphere: (center: [number, number], color?: number) => Object3D | null
  removeHalfSphere: (dome: Object3D) => void
  raycastOrbs: (x: number, y: number) => OrbObject3D | null
  addBillboard: (
    coords: [number, number][],
    hexId: string,
    imageUrl?: string
  ) => Object3D | null
  clearBillboards: () => void
  addDrone: (id: string, config: DroneConfig) => void
  removeDrone: (id: string) => void
  clearDrones: () => void
  raycastDrones: (x: number, y: number) => string | null
  getDrones: () => {id: string; lngLat: [number, number]}[]
  addMiner: (
    id: string,
    lngLat: [number, number],
    hexId: string
  ) => Object3D | null
  clearMiners: () => void
  raycastMiners: (x: number, y: number) => string | null
}

export const MapContext = createContext<MapContextType | null>(null)

export const useMap = (): MapContextType => {
  const context = useContext(MapContext)
  if (!context) {
    throw new Error('useMap must be used within MapProvider')
  }
  return context
}
